import { useCallback, useEffect, useState } from 'react';
import BrandMark from './BrandMark';
import {
  type AccountDto,
  type CompanyDto,
  type FiscalPeriodDto,
  type SessionUserDto,
  type TrialBalanceResponse,
} from '@hesabat/contracts';
import { api, MfaPendingError, UnauthorizedError, type HealthResponse } from './api';
import AccountLedger from './AccountLedger';
import JournalEntry from './JournalEntry';
import Login from './Login';
import Periods from './Periods';
import Reports, { type Tab as ReportsTab } from './Reports';
import ReportsCenter from './ReportsCenter';
import Dashboard from './Dashboard';
import { downloadCsv } from './csv';
import ReportActions from './ReportActions';
import Customers from './Customers';
import SalesInvoices from './SalesInvoices';
import Catalog from './Catalog';
import Inventory from './Inventory';
import CustomerReceipts from './CustomerReceipts';
import PurchaseBills from './PurchaseBills';
import SupplierPayments from './SupplierPayments';
import Suppliers from './Suppliers';
import Treasury from './Treasury';
import CashExpenses from './CashExpenses';
import TreasuryAccounts from './TreasuryAccounts';
import BankReconciliation from './BankReconciliation';
import Settings from './Settings';
import EInvoiceSetup from './EInvoiceSetup';
import Members from './Members';
import ChangePassword from './ChangePassword';
import { formatMoney } from './money';
import MfaChallenge from './MfaChallenge';
import MfaEnroll from './MfaEnroll';
import Platform from './Platform';
import Loading from './Loading';
import type { CenterFilters } from './centerReports';
import Shell from './Shell';
import CompanySetup, { setupDoneKey } from './CompanySetup';
import DataMigration from './DataMigration';
import { Icon } from './icons';
import { navSections, type ViewKind } from './nav';

/** الشاشة الحالية ومعاملاتها. أسماؤها وأيقوناتها ومجموعاتها فى `nav.ts`. */
type View =
  | { kind: 'dashboard' }
  | { kind: 'journal' }
  | { kind: 'ledger'; accountCode: string | null }
  | { kind: 'periods' }
  | { kind: 'reports'; tab?: ReportsTab }
  | { kind: 'reportsCenter'; initial?: Partial<CenterFilters> }
  | { kind: 'customers'; customerId?: string }
  | { kind: 'invoices' }
  | { kind: 'catalog' }
  | { kind: 'inventory' }
  | { kind: 'receipts' }
  | { kind: 'suppliers'; supplierId?: string }
  | { kind: 'bills' }
  | { kind: 'payments' }
  | { kind: 'treasury' }
  | { kind: 'expenses' }
  | { kind: 'treasuryAccounts' }
  | { kind: 'reconciliation'; bankAccountId?: string }
  | { kind: 'settings'; tab?: 'company' | 'einvoice' | 'members' | 'password' }
  | { kind: 'setup' }
  | { kind: 'migration' }
  | { kind: 'platform' };

// كل شاشة لها مدخل فى خريطة القائمة — نوع ناقص هنا أو هناك يُفشل typecheck
const _viewKinds: View['kind'] extends ViewKind
  ? ViewKind extends View['kind']
    ? true
    : never
  : never = true;

/** أنهى المالك معالج الإعداد على هذا المتصفح — فلا تُعرض دعوته ثانية. */
function setupDone(companyId: string): boolean {
  try {
    return window.localStorage.getItem(setupDoneKey(companyId)) === '1';
  } catch {
    return false;
  }
}

function currentYear(): { from: string; to: string } {
  const year = new Date().getFullYear();
  return { from: year + '-01-01', to: year + '-12-31' };
}

interface LoadedState {
  health: HealthResponse | null;
  companies: CompanyDto[];
  accounts: AccountDto[];
  periods: FiscalPeriodDto[];
  trialBalance: TrialBalanceResponse | null;
  /** `settings.inventory.enabled` — شاشات المخزون تظهر حين يفعّله المالك فقط. */
  inventoryEnabled: boolean;
}

const ROLE_LABELS = {
  owner: 'مالك',
  accountant: 'محاسب',
  clerk: 'مُدخل بيانات',
  viewer: 'مشاهد',
} as const;

const EMPTY: LoadedState = {
  health: null,
  companies: [],
  accounts: [],
  periods: [],
  trialBalance: null,
  inventoryEnabled: false,
};

/**
 * حالة الجلسة: مجهولة حتى يردّ /auth/me، ثم إما مستخدم وإما شاشة دخول —
 * أو **جلسة جزئية** ناقصها العامل الثانى، فتُعرض شاشة الرمز أو شاشة التسجيل.
 */
type Session =
  | { status: 'checking' }
  | { status: 'anonymous' }
  | { status: 'mfa_required' }
  | { status: 'mfa_enrollment_required' }
  | { status: 'signed-in'; user: SessionUserDto };

export default function App() {
  const [session, setSession] = useState<Session>({ status: 'checking' });
  const [state, setState] = useState<LoadedState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<View>({ kind: 'dashboard' });
  // يزيد بعد كل ترحيل فيُعاد تحميل الميزان — لا رصيد يُحسب فى الواجهة
  const [reloadKey, setReloadKey] = useState(0);

  // كل شاشة تبدأ من أعلاها
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [view]);

  const checkSession = useCallback(async () => {
    try {
      const user = await api.me();
      setSession({ status: 'signed-in', user });
      // مشغّل المنصة بلا عضوية فى أى شركة لا شاشة له غير لوحة المنصة
      if (user.isPlatformAdmin && user.memberships.length === 0) {
        setView({ kind: 'platform' });
      }
    } catch (caught) {
      // 403 بنوع MFA: الجلسة قائمة وناقصها خطوة — لا تُمسح ولا يُعاد لكلمة السر
      if (caught instanceof MfaPendingError) {
        setSession({ status: caught.status });
      } else if (caught instanceof UnauthorizedError) {
        setSession({ status: 'anonymous' });
      } else {
        setSession({ status: 'anonymous' });
        setError(caught instanceof Error ? caught.message : String(caught));
      }
    }
  }, []);

  useEffect(() => {
    void checkSession();
  }, [checkSession]);

  useEffect(() => {
    if (session.status !== 'signed-in') {
      setState(EMPTY);
      return;
    }

    let cancelled = false;
    setLoading(true);

    async function load() {
      try {
        const health = await api.health();
        if (cancelled) return;
        setState((current) => ({ ...current, health }));

        const companies = await api.companies();
        const first = companies[0];
        if (!first) {
          if (!cancelled) setState((current) => ({ ...current, companies }));
          return;
        }

        const year = currentYear();
        const [accounts, periods, trialBalance, settings] = await Promise.all([
          api.accounts(first.id),
          api.periods(first.id),
          api.trialBalance(first.id, year.from, year.to),
          api.companySettings(first.id),
        ]);

        if (!cancelled) {
          setState({
            health,
            companies,
            accounts,
            periods,
            trialBalance,
            inventoryEnabled: settings.settings.inventory.enabled,
          });
        }
      } catch (caught) {
        if (cancelled) return;
        // انتهاء الجلسة أثناء التصفح يعيد المستخدم لشاشة الدخول لا لرسالة عطل
        if (caught instanceof UnauthorizedError) {
          setSession({ status: 'anonymous' });
          return;
        }
        if (caught instanceof MfaPendingError) {
          setSession({ status: caught.status });
          return;
        }
        setError(caught instanceof Error ? caught.message : String(caught));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [session, reloadKey]);

  const handleSessionLost = useCallback(() => setSession({ status: 'anonymous' }), []);

  async function handleLogout(): Promise<void> {
    try {
      await api.logout();
    } catch {
      // الجلسة منتهية أصلاً — النتيجة واحدة
    }
    setError(null);
    setView({ kind: 'dashboard' });
    setSession({ status: 'anonymous' });
  }

  if (session.status === 'checking') {
    return (
      <div className="boot-screen" role="status">
        <BrandMark />
        <p className="sub">جارى التحقق من الجلسة…</p>
      </div>
    );
  }

  if (session.status === 'anonymous') {
    return <Login onSignedIn={() => void checkSession()} />;
  }

  if (session.status === 'mfa_required') {
    return (
      <MfaChallenge onVerified={() => void checkSession()} onCancel={() => void handleLogout()} />
    );
  }

  if (session.status === 'mfa_enrollment_required') {
    return (
      <MfaEnroll onEnrolled={() => void checkSession()} onCancel={() => void handleLogout()} />
    );
  }

  const company = state.companies[0];
  const tb = state.trialBalance;
  const membership = session.user.memberships.find((item) => item.companyId === company?.id);
  // إخفاء ما سيُرفض راحةٌ للمستخدم لا حماية — الـ API يفرض نفس المصفوفة
  const canClosePeriods = membership ? membership.permissions.includes('period.close') : false;
  const canReopenPeriods = membership ? membership.permissions.includes('period.reopen') : false;
  const canPostDocuments = membership ? membership.permissions.includes('journal.post') : false;
  const canPostManual = membership ? membership.permissions.includes('journal.manual') : false;
  const canManageUsers = membership ? membership.permissions.includes('user.manage') : false;
  const canManageSettings = membership ? membership.permissions.includes('settings.manage') : false;
  const canReadReports = membership ? membership.permissions.includes('report.read') : false;
  const canImportData = membership ? membership.permissions.includes('data.import') : false;
  const openLedger = (accountCode: string | null) => setView({ kind: 'ledger', accountCode });

  const visibleScreens = (kind: ViewKind): boolean => {
    if (kind === 'platform') return session.user.isPlatformAdmin;
    if (!company) return false;
    if (kind === 'journal') return canPostManual;
    if (kind === 'inventory') return state.inventoryEnabled;
    if (kind === 'setup') return canManageSettings;
    if (kind === 'migration') return canImportData;
    return true;
  };

  function selectScreen(kind: ViewKind): void {
    if (kind === 'ledger') openLedger(view.kind === 'ledger' ? view.accountCode : null);
    else setView({ kind } as View);
  }

  return (
    <Shell
      sections={navSections(visibleScreens)}
      current={view.kind}
      tourId={
        view.kind === 'settings' && view.tab && view.tab !== 'company'
          ? (`settings:${view.tab}` as const)
          : view.kind
      }
      onSelect={selectScreen}
      companyName={company ? company.legalNameAr : null}
      userName={session.user.displayName}
      roleLabel={membership ? (membership.customRoleName ?? ROLE_LABELS[membership.role]) : null}
      onLogout={() => void handleLogout()}
      banner={
        membership?.readOnly && view.kind !== 'platform' ? (
          <p className="notice notice-lock">
            <Icon name="lock" size={18} />
            <span>
              الشركة للقراءة فقط (موقوفة أو انتهت فترتها التجريبية): التقارير والتصدير متاحة،
              والإضافة والتعديل ممنوعان حتى التفعيل. تواصل مع مزوّد الخدمة.
            </span>
          </p>
        ) : null
      }
    >
      {loading && <Loading />}

      {view.kind === 'platform' && session.user.isPlatformAdmin && (
        <Platform onSessionLost={handleSessionLost} />
      )}
      {error && (
        <div className="panel">
          <p className="error">تعذّر الاتصال بالخادم: {error}</p>
          {/* أوامر التشغيل للمطوّر وحده — على الإنتاج لا معنى لها لصاحب الشركة */}
          {import.meta.env.DEV ? (
            <p className="sub">
              شغّل القاعدة والخدمة: <code>pnpm db:up</code> ثم <code>pnpm db:migrate</code> ثم{' '}
              <code>pnpm db:seed</code> ثم <code>pnpm dev</code>.
            </p>
          ) : (
            <button type="button" className="ghost" onClick={() => window.location.reload()}>
              <Icon name="refresh" size={18} /> أعد المحاولة
            </button>
          )}
        </div>
      )}

      {company && view.kind === 'journal' && canPostManual && (
        <JournalEntry
          companyId={company.id}
          currency={company.functionalCurrency}
          accounts={state.accounts}
          periods={state.periods}
          onPosted={() => setReloadKey((key) => key + 1)}
          onOpenLedger={openLedger}
        />
      )}

      {company && view.kind === 'reports' && (
        <Reports
          key={reloadKey + '-' + (view.tab ?? 'income')}
          initialTab={view.tab}
          companyId={company.id}
          currency={company.functionalCurrency}
          onOpenLedger={openLedger}
          onOpenCustomer={(customerId) => setView({ kind: 'customers', customerId })}
          onOpenSupplier={(supplierId) => setView({ kind: 'suppliers', supplierId })}
          onSessionLost={handleSessionLost}
        />
      )}

      {company && view.kind === 'reportsCenter' && (
        <ReportsCenter
          key={reloadKey + JSON.stringify(view.initial ?? {})}
          companyId={company.id}
          userId={session.user.id}
          currency={company.functionalCurrency}
          onOpenLedger={openLedger}
          onOpenCustomer={(customerId) => setView({ kind: 'customers', customerId })}
          onOpenSupplier={(supplierId) => setView({ kind: 'suppliers', supplierId })}
          onNavigate={(target) => setView(target)}
          canReadAuditLog={canManageUsers}
          initial={view.initial}
          onSessionLost={handleSessionLost}
        />
      )}

      {company && view.kind === 'customers' && (
        <Customers
          key={view.customerId ?? 'list'}
          companyId={company.id}
          currency={company.functionalCurrency}
          canCreate={canPostDocuments}
          initialCustomerId={view.customerId ?? null}
          onSessionLost={handleSessionLost}
        />
      )}

      {company && view.kind === 'invoices' && (
        <SalesInvoices
          companyId={company.id}
          currency={company.functionalCurrency}
          accounts={state.accounts}
          canIssue={canPostDocuments}
          canCancel={canPostManual}
          onSessionLost={handleSessionLost}
        />
      )}

      {company && view.kind === 'catalog' && (
        <Catalog
          companyId={company.id}
          accounts={state.accounts}
          canEdit={canPostDocuments}
          inventoryEnabled={state.inventoryEnabled}
          canPostOpening={canPostManual}
          onSessionLost={handleSessionLost}
        />
      )}

      {company && view.kind === 'inventory' && state.inventoryEnabled && (
        <Inventory
          key={reloadKey}
          companyId={company.id}
          currency={company.functionalCurrency}
          canPost={canPostManual}
          onPosted={() => setReloadKey((key) => key + 1)}
          onOpenLedger={openLedger}
          onSessionLost={handleSessionLost}
        />
      )}

      {company && view.kind === 'suppliers' && (
        <Suppliers
          key={view.supplierId ?? 'list'}
          companyId={company.id}
          currency={company.functionalCurrency}
          canCreate={canPostDocuments}
          initialSupplierId={view.supplierId ?? null}
          onSessionLost={handleSessionLost}
        />
      )}

      {company && view.kind === 'bills' && (
        <PurchaseBills
          companyId={company.id}
          currency={company.functionalCurrency}
          accounts={state.accounts}
          canRecord={canPostDocuments}
          canCancel={canPostManual}
          inventoryEnabled={state.inventoryEnabled}
          onSessionLost={handleSessionLost}
        />
      )}

      {company && view.kind === 'payments' && (
        <SupplierPayments
          companyId={company.id}
          currency={company.functionalCurrency}
          accounts={state.accounts}
          canRecord={canPostDocuments}
          onSessionLost={handleSessionLost}
        />
      )}

      {company && view.kind === 'expenses' && (
        <CashExpenses
          companyId={company.id}
          currency={company.functionalCurrency}
          accounts={state.accounts}
          canRecord={canPostDocuments}
          onSessionLost={handleSessionLost}
        />
      )}

      {company && view.kind === 'settings' && (
        <>
          <div className="tabs">
            <button
              type="button"
              className={'tab' + ((view.tab ?? 'company') === 'company' ? ' active' : '')}
              onClick={() => setView({ kind: 'settings', tab: 'company' })}
            >
              الشركة والخيارات
            </button>
            <button
              type="button"
              className={'tab' + (view.tab === 'einvoice' ? ' active' : '')}
              onClick={() => setView({ kind: 'settings', tab: 'einvoice' })}
            >
              الفاتورة الإلكترونية
            </button>
            {canManageUsers && (
              <button
                type="button"
                className={'tab' + (view.tab === 'members' ? ' active' : '')}
                onClick={() => setView({ kind: 'settings', tab: 'members' })}
              >
                المستخدمون والأدوار
              </button>
            )}
            <button
              type="button"
              className={'tab' + (view.tab === 'password' ? ' active' : '')}
              onClick={() => setView({ kind: 'settings', tab: 'password' })}
            >
              كلمة السر
            </button>
          </div>
          {(view.tab ?? 'company') === 'company' && (
            <Settings
              companyId={company.id}
              canEdit={canManageSettings}
              onSaved={() => setReloadKey((key) => key + 1)}
              onSessionLost={handleSessionLost}
            />
          )}
          {view.tab === 'einvoice' && (
            <EInvoiceSetup
              companyId={company.id}
              canManage={canManageSettings}
              canReadReports={canReadReports}
              onSessionLost={handleSessionLost}
            />
          )}
          {view.tab === 'members' && canManageUsers && (
            <Members
              companyId={company.id}
              currentUserId={session.user.id}
              onSessionLost={handleSessionLost}
            />
          )}
          {view.tab === 'password' && <ChangePassword onSessionLost={handleSessionLost} />}
        </>
      )}

      {company && view.kind === 'treasuryAccounts' && (
        <TreasuryAccounts
          companyId={company.id}
          currency={company.functionalCurrency}
          accounts={state.accounts}
          canManage={canPostManual}
          onOpenLedger={openLedger}
          onReconcile={(bankAccountId) => setView({ kind: 'reconciliation', bankAccountId })}
          onAccountsChanged={() => setReloadKey((key) => key + 1)}
          onSessionLost={handleSessionLost}
        />
      )}

      {company && view.kind === 'reconciliation' && (
        <BankReconciliation
          key={view.bankAccountId ?? 'default'}
          companyId={company.id}
          currency={company.functionalCurrency}
          canReconcile={canPostManual}
          initialBankAccountId={view.bankAccountId ?? null}
          onSessionLost={handleSessionLost}
        />
      )}

      {company && view.kind === 'treasury' && (
        <Treasury
          companyId={company.id}
          currency={company.functionalCurrency}
          accounts={state.accounts}
          canRecord={canPostDocuments}
          onOpenLedger={openLedger}
          onSessionLost={handleSessionLost}
        />
      )}

      {company && view.kind === 'receipts' && (
        <CustomerReceipts
          companyId={company.id}
          currency={company.functionalCurrency}
          accounts={state.accounts}
          canRecord={canPostDocuments}
          onSessionLost={handleSessionLost}
        />
      )}

      {company && view.kind === 'periods' && (
        <Periods
          companyId={company.id}
          periods={state.periods}
          canManage={canClosePeriods}
          canReopen={canReopenPeriods}
          onChanged={() => setReloadKey((key) => key + 1)}
        />
      )}

      {company && view.kind === 'ledger' && (
        <AccountLedger
          // مفتاح التحديث يُعيد تحميل الكشف بعد ترحيل جديد
          key={reloadKey}
          companyId={company.id}
          currency={company.functionalCurrency}
          accounts={state.accounts}
          accountCode={view.accountCode}
          onChangeAccount={(code) => openLedger(code || null)}
          onSessionLost={handleSessionLost}
        />
      )}

      {company && view.kind === 'migration' && canImportData && (
        <DataMigration
          companyId={company.id}
          companyName={company.legalNameAr}
          onSessionLost={handleSessionLost}
        />
      )}

      {company && view.kind === 'setup' && canManageSettings && (
        <CompanySetup
          companyId={company.id}
          currency={company.functionalCurrency}
          currentUserId={session.user.id}
          accounts={state.accounts}
          periods={state.periods}
          canEditSettings={canManageSettings}
          canManageUsers={canManageUsers}
          canPostManual={canPostManual}
          canClosePeriods={canClosePeriods}
          canReopenPeriods={canReopenPeriods}
          onChanged={() => setReloadKey((key) => key + 1)}
          onDone={() => setView({ kind: 'dashboard' })}
          onOpenLedger={openLedger}
          onSessionLost={handleSessionLost}
        />
      )}

      {view.kind === 'dashboard' && (company || !session.user.isPlatformAdmin) && (
        <>
          {/* شركة بلا قيد هذا العام ولم يُكمل مالكها الإعداد: دعوة واحدة للمعالج */}
          {company && canManageSettings && tb && tb.rows.length === 0 && !setupDone(company.id) && (
            <button
              type="button"
              className="setup-invite card-link"
              onClick={() => setView({ kind: 'setup' })}
            >
              <span className="setup-invite-icon" aria-hidden="true">
                <Icon name="sparkle" size={22} />
              </span>
              <span>
                <strong>ابدأ إعداد شركتك</strong>
                <span className="sub">
                  البيانات، الهوية، السنة المالية، الأرصدة الافتتاحية، والمستخدمون — فى معالج واحد.
                </span>
              </span>
              <Icon name="chevron" />
            </button>
          )}
          {company && (
            <Dashboard
              key={reloadKey}
              companyId={company.id}
              currency={company.functionalCurrency}
              userName={session.user.displayName}
              canReadReports={canReadReports}
              canManageSettings={canManageSettings}
              inventoryEnabled={state.inventoryEnabled}
              onNavigate={(target) => setView(target)}
              onSessionLost={handleSessionLost}
            />
          )}

          {canReadReports && (
            /* الميزان للمحاسب: مطوى افتراضيًا حتى لا يطيل الرئيسية — يُفتح بضغطة */
            <details className="panel tb-details">
              <summary>
                <h2>ميزان المراجعة</h2>
                <span className="sub">كل الحسابات بأرصدتها هذا العام</span>
              </summary>
              <div className="panel-head">
                {tb && tb.rows.length > 0 && (
                  <ReportActions
                    onExport={() =>
                      downloadCsv(
                        'ميزان-المراجعة-' + tb.from + '-' + tb.to + '.csv',
                        ['الكود', 'الحساب', 'مدين', 'دائن', 'الرصيد'],
                        [
                          ...tb.rows.map((row) => [
                            row.accountCode,
                            row.nameAr,
                            row.debit,
                            row.credit,
                            row.balance,
                          ]),
                          ['', 'الإجمالى', tb.totalDebit, tb.totalCredit, ''],
                        ],
                      )
                    }
                  />
                )}
              </div>
              <p className="sub">اضغط على أى حساب لفتح كشفه.</p>
              {tb && tb.rows.length > 0 ? (
                <table>
                  <thead>
                    <tr>
                      <th>الكود</th>
                      <th>الحساب</th>
                      <th>مدين</th>
                      <th>دائن</th>
                      <th>الرصيد</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tb.rows.map((row) => (
                      <tr
                        key={row.accountCode}
                        className="clickable"
                        tabIndex={0}
                        onClick={() => openLedger(row.accountCode)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') openLedger(row.accountCode);
                        }}
                      >
                        <td>{row.accountCode}</td>
                        <td>{row.nameAr}</td>
                        <td className="num-cell">{formatMoney(row.debit)}</td>
                        <td className="num-cell">{formatMoney(row.credit)}</td>
                        <td className="num-cell">{formatMoney(row.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="sub">
                  لا توجد قيود منشورة بعد
                  {canPostManual ? ' — ابدأ من «قيد يومية» فى القائمة.' : '.'}
                </p>
              )}
            </details>
          )}
        </>
      )}
    </Shell>
  );
}
